import React, { useEffect, useMemo } from 'react'
import NextImage from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'

import { Text } from '@wartek-id/text'
import { Icon } from '@wartek-id/icon'
import { Button } from '@wartek-id/button-v1'

import Header from 'app/BuktiKaryaPage/components/Header'
import useKSPSTrackEvent from './hooks/useKSPSTrackEvent'
import {
  useQueryAPIGetKSSelectionCandidate,
  useQueryAPIGetKSSelectionProposal,
} from 'api/bcks/useAPIBCKSInvitation'
import { formatDate } from 'utils/datetime'
import { useAPIGetKepalaSekolahEligibility } from 'api/bcks/useAPIBCKSQualification'
import { SelectionStepsV2 } from './components/SelectionSteps/SelectionStepsV2'

export const requirementList = [
  'Memiliki kualifikasi akademik paling rendah S1 atau D4',
  'Memiliki sertifikat pendidik',
  'Memiliki pangkat paling rendah Penata dan golongan ruang III/c bagi Guru yang berstatus PNS',
  'Memiliki jenjang jabatan paling rendah Guru Ahli Pertama',
  'Untuk Guru Ahli Pertama berstatus PPPK: memiliki pengalaman sebagai Guru paling sedikit 8 tahun',
  'Berusia kurang dari 56 tahun',
]

export const requirementListV2 = [
  'Memiliki kualifikasi akademik paling rendah sarjana (S-1) atau diploma empat (D-IV) dari perguruan tinggi dan program studi yang terakreditasi;',
  'Memiliki sertifikat pendidik;',
  'Memiliki pangkat dan golongan ruang paling rendah penata, III/c bagi Guru yang berstatus sebagai PNS;',
  'Memiliki jenjang jabatan paling rendah Guru ahli pertama bagi Guru yang berstatus sebagai PPPK dengan pengalaman dalam jabatan sebagai Guru paling sedikit 8 (delapan) tahun;',
  'Memiliki hasil penilaian kinerja Guru dengan predikat paling rendah "Baik" selama 2 (dua) tahun terakhir;',
  'Memiliki pengalaman manajerial paling singkat 2 (dua) tahun di Satuan Pendidikan, organisasi pendidikan, dan/atau komunitas pendidikan;',
  'Tidak pernah dikenai hukuman disiplin sedang dan/atau berat sesuai dengan ketentuan peraturan perundang-undangan;',
  'Tidak sedang menjadi tersangka, terdakwa, atau tidak pernah menjadi terpidana;',
  'Berusia paling tinggi 56 (lima puluh enam) tahun pada saat diberi penugasan sebagai Kepala Sekolah; dan',
  'Menandatangani pakta integritas bersedia ditempatkan di wilayah yang menjadi kewenangan Pemerintah Daerah terkait.',
]

export const SelectionPage = () => {
  const router = useRouter()
  const { KSPSTrackEvent } = useKSPSTrackEvent()

  const { data: candidateData, isError: isErrorGetCandidate } =
    useQueryAPIGetKSSelectionCandidate()
  const proposalParam = { version: 2 }
  const { data: proposalData } = useQueryAPIGetKSSelectionProposal(
    {
      staleTime: 60 * 60 * 1000,
      useErrorBoundary: false,
    },
    proposalParam
  )
  const { data: eligibleData } = useAPIGetKepalaSekolahEligibility()

  const isKS = eligibleData?.currentAssignment.periodePenugasan > 0

  const previewJuknisURLQuery = {
    title: encodeURI(
      'Salinan akhir Perdirjen Juknis Penugasan Guru Sebagai Kepala Sekolah'
    ),
    docUrl:
      'https://cdn.guru-ks-fe.belajar.id/documents/Juknis%20Seleksi%20Kepala%20Sekolah.pdf',
  }

  const shouldShowButtonProposalKS = useMemo(() => {
    const hasCandidateToPropose =
      proposalData?.proposalStatus === 'not_selected' &&
      proposalData?.candidates?.length > 0

    if (isErrorGetCandidate) {
      return hasCandidateToPropose
    }

    return (
      candidateData?.selectionCandidateState === 'notInvitedKS' &&
      hasCandidateToPropose
    )
  }, [isErrorGetCandidate, candidateData, proposalData])

  useEffect(() => {
    KSPSTrackEvent({
      event_name: 'enter_ks_selection_page',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="relative flex flex-col h-screen max-w-xl mx-auto">
      <Header
        title={'Seleksi/Pelatihan BCKS'}
        forceNavigateBack
        variant="large"
      />

      <div className="px-4 pt-4">
        {['notInvitedGuru', 'notInvitedKS'].includes(
          candidateData?.selectionCandidateState
        ) &&
          candidateData?.startDate &&
          candidateData?.endDate && (
            <div className="flex flex-col gap-2 mb-11">
              <Text variant="heading-sm">Pengumuman</Text>

              <div className="rounded-lg shadow p-4 flex justify-between space-x-4 bg-white">
                <Text>
                  Dinas Pendidikan {candidateData?.schoolCity} membuka
                  pendaftaran Seleksi/Pelatihan BCKS pada:{' '}
                  <b>
                    {formatDate(candidateData?.startDate)} -{' '}
                    {formatDate(candidateData?.endDate)}
                  </b>
                </Text>
                <div className="flex flex-col justify-center shrink-0">
                  <NextImage
                    src="/images/bcks/img-collect-files-folder.png"
                    width={60}
                    height={60}
                    alt="Seleksi Kepala Sekolah"
                  />
                </div>
              </div>
            </div>
          )}

        <div>
          <div className="flex flex-col gap-1">
            <Text variant="heading-sm">Juknis Seleksi</Text>
            <Text>
              Sesuai petunjuk teknis terbaru, berikut kriteria wajib dan tahapan
              lengkap seleksi Kepala Sekolah. Seleksi akan dilakukan di Ruang
              GTK.
            </Text>
          </div>
          <Link
            passHref
            href={`/bcks/invitation/guide?title=${previewJuknisURLQuery.title}&docUrl=${previewJuknisURLQuery.docUrl}`}
          >
            <Button
              size="sm"
              className="mt-4"
              fullWidth
              color="white"
              onClick={() => {
                KSPSTrackEvent({
                  event_name: 'click_ks_selection_read_latest_juknis',
                })
              }}
            >
              Baca Juknis Terbaru <Icon>chevron_right</Icon>
            </Button>
          </Link>
        </div>

        <div className="flex flex-col mt-7 mb-10">
          <Text variant="heading-sm">
            Persyaratan sebagai Bakal Calon Kepala Sekolah
          </Text>
          <Text className="mt-1 mb-4">
            Berdasarkan pasal 7 dan 32 Permendikdasmen no 7 tahun 2025
          </Text>
          <ul className="pl-8 list-disc list-outside">
            {requirementListV2.map((requirement, index) => (
              <li key={index}>
                <Text>{requirement}</Text>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <Text variant="heading-sm">Tahapan Seleksi</Text>

      <div className="mt-3">
        <SelectionStepsV2 staticView />
      </div>

      {['notInvitedGuru', 'notInvitedKS'].includes(
        candidateData?.selectionCandidateState
      ) &&
        candidateData?.invitationFileUrl && (
          <div className="px-4">
            <Text variant="heading-sm">Pengumuman</Text>
            <div className="flex gap-3 items-start mt-3">
              <a
                target="_blank"
                href={candidateData?.invitationFileUrl}
                rel="noreferrer"
                className="flex !shrink-0"
              >
                <NextImage
                  src="/images/bcks/img-invitation-doc.png"
                  alt={'image-placeholder'}
                  width={72}
                  height={72}
                />
              </a>
              <div>
                <Text variant="body">
                  Dokumen pengumuman Seleksi/Pelatihan BCKS
                </Text>

                <Link
                  href={`/bcks/invitation/guide?title=%20Surat%20Resmi%20Pengumuman%20Seleksi%20Kepala%20Sekolah&signedDocUrl=true&docUrl=${candidateData?.invitationFileUrl}`}
                >
                  <Button variant="ghost" className="pt-2 !px-0">
                    Lihat Dokumen
                    <Icon color="informational">chevron_right</Icon>
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}

      <div className="px-4 mt-11 pb-5">
        <Text variant="heading-sm">Cek kualifikasi Anda?</Text>
        <Text variant="body" className="mt-2">
          {isKS
            ? 'Cek profil Anda selama menjabat sebagai kepala sekolah'
            : 'Cek apakah Anda memenuhi kriteria seleksi Kepala Sekolah. Aksi ini hanya bersifat pengecekan, bukan untuk mengikuti seleksi.'}
        </Text>
        <Button
          className="mt-4"
          fullWidth
          size="lg"
          onClick={() => {
            router.push('/bcks/qualification-check')
            KSPSTrackEvent({
              event_name: 'click_ks_selection_eligibility_check',
            })
          }}
        >
          {isKS ? 'Cek profil' : 'Cek kualifikasi'}
        </Button>

        {candidateData?.selectionCandidateState === 'notInvitedGuru' && (
          <Button
            className="mt-3"
            size="lg"
            fullWidth
            color="black"
            onClick={() => router.push('/bcks/self-proposal')}
          >
            Lihat cara usulkan diri
          </Button>
        )}
      </div>

      {shouldShowButtonProposalKS && (
        <div className="mt-5 px-4 pb-5">
          <Text variant="heading-sm">Pengusulan</Text>
          <Button
            className="mt-3"
            size="lg"
            fullWidth
            color="black"
            onClick={() => router.push('/bcks/ks-proposal')}
          >
            Lihat cara pengusulan diri dan Guru
          </Button>
        </div>
      )}
    </div>
  )
}
